import { useEffect, useState } from 'react';
import {
  SnowflakeContainer,
  LabelsContainer,
  SnowflakeWrapper,
  ActionsWrapper,
  MainTitle,
  CopyIcon,
  Title,
  TitleLight,
} from './styled';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
} from '@/constants/anchors';
import loadable from '@loadable/component';
import useCopyUrlToClipboard from '@/pages/CompanyReport/hooks/useCopyUrlToClipboard';
import { SidebarActions } from '@/pages/CompanyReport/components/Overview/components/SidebarActions';
import { useAnalysis, useSummaryV2 } from '@/hooks/useCompany';
import { useTranslation } from '@/hooks/useTranslation';

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));

const snowFlakeRoutes = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

function targetIsDocument(target: EventTarget | null): target is Document {
  if (target) {
    return target instanceof Document;
  }
  return false;
}

const minY = 50;
const maxY = 220;

interface Props {
  onClick?: (route: string) => void;
  keepSnowflakePinned?: boolean;
}
export function FadingSnowflake({ onClick, keepSnowflakePinned }: Props) {
  const [opacity, setOpacity] = useState(0);
  const copyUrlToClipboard = useCopyUrlToClipboard();
  const summary = useSummaryV2();
  const analysis = useAnalysis();

  const t = useTranslation();

  useEffect(() => {
    const onScroll = (event: Event) => {
      const { target } = event;
      if (targetIsDocument(target)) {
        const { scrollTop } = target.documentElement;
        const minDiff = scrollTop - minY;
        const maxDiff = maxY - minY;
        setOpacity(Math.min((minDiff < 0 ? 0 : minDiff / maxDiff) * 1, 1));
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (summary.status !== 'success') {
    return null;
  }
  return (
    <>
      <SnowflakeContainer visibility={keepSnowflakePinned ? 1 : opacity}>
        <SnowflakeWrapper data-cy-id="desktop-sidebar-snowflake">
          <Snowflake
            points={summary.score}
            tooltip={true}
            routes={snowFlakeRoutes}
            handleClick={onClick}
            type={SnowflakeType.TOC}
          />
        </SnowflakeWrapper>
      </SnowflakeContainer>
      <LabelsContainer visibility={keepSnowflakePinned ? 1 : opacity}>
        <MainTitle data-cy-id="desktop-sidebar-company-name">
          {summary.name}
          <span>
            <CopyIcon onClick={copyUrlToClipboard} width={24} height={24} />
          </span>
        </MainTitle>
        <Title data-cy-id="desktop-sidebar-title">
          {t('page.companyReport.header.companyTitle', {
            uniqueSymbol: summary.uniqueSymbol,
          })}
        </Title>
        {analysis.status === 'success' ? (
          <TitleLight data-cy-id="desktop-sidebar-market-cap">
            {t('page.companyReport.header.metadata.marketCapShort', {
              marketCap: analysis.formattedMarketCap,
            })}
          </TitleLight>
        ) : null}
        <ActionsWrapper>
          <SidebarActions />
        </ActionsWrapper>
      </LabelsContainer>
    </>
  );
}
